import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Github, Linkedin, MapPin, Send } from "lucide-react";

export default function Contact() {
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(
        "https://formsubmit.co/ajax/sujitaregmi189@gmail.com",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Message could not be sent.");
      }

      setStatus({
        type: "success",
        message: "Message sent successfully.",
      });

      form.reset();
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "There was a problem sending your message.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-gray-50 dark:bg-[#030712] py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="uppercase tracking-[0.3em] text-emerald-500 font-semibold">
            Contact
          </p>

          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
            Let's Work Together
          </h2>

          <p className="mt-5 max-w-2xl mx-auto text-gray-600 dark:text-gray-400 leading-8">
            Whether you have a project in mind, a collaboration opportunity, or
            simply want to connect, I'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-14">
          {/* Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                Get In Touch
              </h3>

              <p className="mt-5 leading-8 text-gray-600 dark:text-gray-400">
                I'm always open to discussing new opportunities, freelance work,
                innovative ideas, or simply having a conversation about
                technology.
              </p>

              <div className="mt-10 space-y-6">
                <a
                  href="mailto:sujitaregmi189@gmail.com"
                  className="flex items-center gap-4 group"
                >
                  <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/20 p-4">
                    <Mail className="text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Email</p>

                    <p className="font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 transition">
                      sujitaregmi189@gmail.com
                    </p>
                  </div>
                </a>

                <a
                  href="https://github.com/sujitaregmi777"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/20 p-4">
                    <Github className="text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">GitHub</p>

                    <p className="font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 transition">
                      github.com/sujitaregmi777
                    </p>
                  </div>
                </a>

                <a
                  href="https://www.linkedin.com/in/sujita-regmi-618b382a7/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/20 p-4">
                    <Linkedin className="text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">LinkedIn</p>

                    <p className="font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 transition">
                      Connect with me
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/20 p-4">
                    <MapPin className="text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Location</p>

                    <p className="font-semibold text-gray-900 dark:text-white">
                      Nepal
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-lg space-y-6"
            >
              <input type="hidden" name="_captcha" value="false" />

              <div>
                <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                  Full Name
                </label>

                <input
                  type="text"
                  name="name"
                  required
                  placeholder="John Doe"
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-5 py-4 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                  Email Address
                </label>

                <input
                  type="email"
                  name="email"
                  required
                  placeholder="john@example.com"
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-5 py-4 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                  Subject
                </label>

                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="Project Discussion"
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-5 py-4 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                  Message
                </label>

                <textarea
                  name="message"
                  rows="6"
                  required
                  placeholder="Tell me about your project..."
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-5 py-4 resize-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 outline-none transition"
                />
              </div>

              {status && (
                <p
                  className={`text-sm font-medium ${
                    status.type === "success"
                      ? "text-emerald-600"
                      : "text-red-600"
                  }`}
                  aria-live="polite"
                >
                  {status.message}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-3 rounded-xl bg-emerald-600 px-8 py-4 font-semibold text-white transition duration-300 hover:bg-emerald-500 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              >
                <Send size={18} />
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
